import "./Purchase.css";
import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AiOutlineDown, AiOutlineUp } from "react-icons/ai";
import { AuthContext } from "../Auth.js";
import { SyncLoader } from "react-spinners";
import { Nav } from "../components/home-page/nav-section";
import { Footer } from "../components/home-page/footer-section";
import { getCheckoutUrl, subscriptionIds } from "../functions/stripeCheckout.js";

// Per-tier marketing copy. Prices / seats come from subscriptionIds (the same
// source the webhook keys off) so they can never drift apart.
const TIERS = [
  {
    key: "parents",
    name: "Parents",
    tagline: "For home use",
    features: [
      "A focused set of 24 activities for use at home",
      "1 user account",
      "Yearly subscription — auto-renews",
      "Tech support to keep Piggyback Joy working",
    ],
    featured: false,
  },
  {
    key: "regular",
    name: "Regular",
    tagline: "For practices",
    features: [
      "Full access to all Piggyback Joy activities",
      "Up to 3 verified user accounts within the same business",
      "Yearly subscription — auto-renews",
      "Tech support to keep Piggyback Joy working",
    ],
    featured: false,
  },
  {
    key: "premium",
    name: "Premium",
    tagline: "Everything, plus mentoring",
    features: [
      "Full access to all Piggyback Joy activities",
      "Up to 3 verified user accounts within the same business",
      "Full access to our Online Academy — videos, handouts, assessment tools",
      "12 monthly individualised 60-minute mentoring / training sessions",
      "Yearly subscription — auto-renews",
      "Tech support to keep Piggyback Joy working",
    ],
    featured: true,
  },
];

export default function Purchase() {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [equipmentOpen, setEquipmentOpen] = useState(false);

  async function initiateCheckout(tierKey) {
    if (!currentUser) {
      navigate("/login");
      return;
    }
    setLoading(true);

    const tier = subscriptionIds[tierKey];
    const priceData = [{ price: tier.id, quantity: 1 }];
    const fee = calculateFee(tier.price);
    // Seat count is derived server-side from the purchased price (PRICE_TO_TIER
    // in functions/index.js); the buyer assigns seats AFTER checkout.
    const checkoutURL = await getCheckoutUrl(priceData, fee);
    setLoading(false);
    window.location.href = checkoutURL;
  }

  function calculateFee(price) {
    let totalWithFee = (price + 0.3) / (1 - 0.029);
    let fee = totalWithFee - price;
    return fee;
  }

  if (loading) {
    return (
      <div className="loadingScreen">
        <SyncLoader color={"#e7b33e"} loading={loading} size={20} />
      </div>
    );
  }

  return (
    <div id="outerContainer">
      <Nav />
      <div>
        <div id="purchaseHeader">
          <h1 className="fontheader">Choose your Piggyback Joy plan</h1>
        </div>
      </div>

      <div className="tierCardGrid">
        {TIERS.map((tier) => {
          const meta = subscriptionIds[tier.key];
          return (
            <div
              key={tier.key}
              className={`tierCard${tier.featured ? " featured" : ""}`}
            >
              {tier.featured && <div className="tierBadge">Most popular</div>}
              <h2 className="tierName">{tier.name}</h2>
              <p className="tierTagline">{tier.tagline}</p>
              <p className="tierPrice">
                <span className="tierPriceAmount">${meta.price}</span>
                <span className="tierPricePer"> AUD / year</span>
              </p>
              <p className="tierSeats">
                {meta.seats} {meta.seats === 1 ? "user account" : "user accounts"}
              </p>
              <button
                type="button"
                className="tierBuyButton"
                onClick={() => initiateCheckout(tier.key)}
              >
                Buy {tier.name}
              </button>
              <ul className="tierFeatures">
                {tier.features.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <div className="equipmentSection">
        <button
          type="button"
          className="equipmentToggle"
          onClick={() => setEquipmentOpen(!equipmentOpen)}
          aria-expanded={equipmentOpen}
        >
          <h3 className="fontheader">Custom equipment for PiggybackJoy:</h3>
          {equipmentOpen ? <AiOutlineUp /> : <AiOutlineDown />}
        </button>
        {equipmentOpen && (
          <ul className="listText equipmentList">
            <li>
              <strong>Aussie Animal Tangram Book</strong>
              <ul>
                <li>Our own design</li>
                <li>A playful way to develop advanced visualisation skills</li>
                <li>2 sets of wooden Tangram blocks</li>
                <li>Hand coloured with non-toxic paint</li>
                <li>Each set of seven comes in a canvas bag</li>
                <li>Custom design shape and colours</li>
                <li>Made to play with Piggyback Joy games</li>
              </ul>
            </li>
            <li>
              <strong>Pegboards</strong>
              <ul>
                <li>2 sturdy pegboards (5 and 9 hole)</li>
                <li>Custom designed for Piggyback Joy games</li>
                <li>Clear material speeds learning - directly check answers</li>
                <li>Develops visual memory from simple to complex</li>
              </ul>
            </li>
            <li>
              <strong>Pegs</strong>
              <ul>
                <li>Wooden pegs with easy grip tops</li>
                <li>Four different colours to match Piggyback games</li>
                <li>Full set of 3 each colour</li>
                <li>Easy play with high contrast colours</li>
                <li>Size and shape ideal for building visually directed fine motor skills</li>
              </ul>
            </li>
            <li>
              <strong>Letter LeapFrog Croaky Charts</strong>
              <ul>
                <li>Sturdy handheld letter charts</li>
                <li>Fun and silly poems appeal to the emerging reader</li>
                <li>Builds eye movement control and sight word vocabulary</li>
                <li>Integrated with Piggyback Joy</li>
                <li>Visual cues direct attention to improve reading speed</li>
              </ul>
            </li>
            <li>
              <strong>3D glasses with specific colours to work with Piggyback images</strong>
              <ul>
                <li>Tranaglyphs/Anaglyphs</li>
                <li>Free space image fusion</li>
                <li>Progression from simple to complex binocular function</li>
                <li>High quality custom artwork</li>
                <li>Playful images designed to keep children engaged</li>
                <li>Interactive features</li>
                <li>Australian focus: Wildlife, our natural world, and sport including Australian Rules Football</li>
              </ul>
            </li>
            <li>
              <strong>Sticks and beads</strong>
              <ul>
                <li>Simple matching sticks and beads</li>
                <li>Easier to use than threading beads onto string</li>
                <li>Sturdy design for remedial skills</li>
                <li>Simple enough for very young child</li>
                <li>Develops visually directed fine motor</li>
                <li>Improves visual memory</li>
              </ul>
            </li>
            <li>
              <strong>Stick and Straw/beanbags</strong>
              <ul>
                <li>Join the Circus program teaches juggling</li>
                <li>Developed in collaboration with our local Circus School</li>
                <li>Learn directly from a Flying Fruit Fly Circus Instructor</li>
                <li>Non-toxic stick and straw game develops eye movement control</li>
                <li>Fun, non-confrontational skill improves near vision in children with convergence insufficiency</li>
                <li>Bean bags made from non-toxic materials</li>
                <li>Easily fit into small hands</li>
                <li>Fun colours</li>
                <li>Builds 3D map and directs visual attention in non-threatening progression</li>
              </ul>
            </li>
            <li>
              <strong>Ball on String</strong>
              <ul>
                <li>Effective for reducing startle response</li>
                <li>Learn to identify numbers</li>
                <li>Direct visual attention</li>
                <li>Build 3D spatial awareness</li>
              </ul>
            </li>
            <li>
              <strong>Beads on String</strong>
              <ul>
                <li>Colourful beads on 3 metre string</li>
                <li>Used for vergence jumps through space</li>
                <li>Builds binocular skills</li>
                <li>Develops eye movement control and accuracy</li>
              </ul>
            </li>
            <li>
              <strong>SuperVision Cards</strong>
              <ul>
                <li>Advanced binocular skills</li>
                <li>Fun custom designed wedge tailed eagles</li>
                <li>Free space fusion progression</li>
              </ul>
            </li>
          </ul>
        )}
      </div>

      <div className="purchaseContact">
        <p className="listText">
          Want to take Piggyback Joy out for a test drive before you decide, or
          need replacement equipment? Email us at{" "}
          <a href="mailto:reframingliteracy@gmail.com">reframingliteracy@gmail.com</a>
        </p>
      </div>

      <Footer />
    </div>
  );
}
